// ─────────────────────────────────────────────────────────────
// RestoreAccountGate
//
// Shown when a user signs in and their profile has deleted_at set.
// Offers either to restore the account (clears deleted_at) or sign
// out and let the deletion proceed.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { C } from "../../lib/colors";
import { Btn } from "../../components/ui/Btn";

const GRACE_PERIOD_DAYS = 30;

export function RestoreAccountGate({ profile, onRestored, onSignOut }) {
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  const deletedAt = new Date(profile.deleted_at);
  const purgeAt   = new Date(deletedAt.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
  const now       = new Date();
  const daysLeft  = Math.max(0, Math.ceil((purgeAt - now) / (1000 * 60 * 60 * 24)));

  const handleRestore = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/functions/v1/restore-account`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Restore failed");
      // Show the success screen with subscription-status info, then the
      // user clicks Continue to actually re-enter the app.
      setRestoreResult(data);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    // Refresh profile so the app un-gates.
    await onRestored?.();
  };

  // ── Post-restore success screen ─────────────────────────────────────────
  // Shows what happened to the subscription before continuing into the app.
  if (restoreResult?.success && !restoreResult.already_active) {
    const reactivated = restoreResult.subscription_reactivated;
    const status = restoreResult.subscription_status;
    const wasOnTrial = status === "trialing";
    const wasActive  = status === "active";

    return (
      <div style={{
        position: "fixed", inset: 0,
        background: "#0a0c18",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}>
        <div style={{
          maxWidth: 480,
          width: "100%",
          background: "#0f1322",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 32,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>✓</div>

          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.green, marginBottom: 12 }}>
            Account restored
          </h1>

          <p style={{ fontSize: 14, color: C.sub, lineHeight: 1.55, marginBottom: 14 }}>
            Your trades, accounts, strategies, and settings are back.
          </p>

          {/* Subscription status messaging — varies by what happened */}
          {reactivated && wasOnTrial && (
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
              Your <strong style={{ color: C.text }}>free trial</strong> has been
              reactivated and will continue as scheduled. You can manage your
              subscription anytime from <strong>Billing</strong>.
            </p>
          )}

          {reactivated && wasActive && (
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
              Your <strong style={{ color: C.text }}>subscription</strong> has been
              reactivated. Billing will continue as normal. You can manage your
              subscription anytime from <strong>Billing</strong>.
            </p>
          )}

          {!reactivated && (
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
              Your subscription was no longer active. To regain full access,
              start a new subscription from <strong>Billing</strong>.
            </p>
          )}

          <button
            onClick={handleContinue}
            style={{
              marginTop: 6,
              padding: "12px 28px",
              background: C.blue,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              minWidth: 200,
            }}
          >
            Continue to app
          </button>
        </div>
      </div>
    );
  }

  // ── Main "welcome back" screen ──────────────────────────────────────────
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "#0a0c18",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
      zIndex: 9999,
    }}>
      <div style={{
        maxWidth: 480,
        width: "100%",
        background: "#0f1322",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32,
        textAlign: "center",
      }}>
        <div style={{ fontSize: 40, marginBottom: 14 }}>🕰️</div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 12 }}>
          Welcome back
        </h1>

        <p style={{ fontSize: 14, color: C.sub, lineHeight: 1.55, marginBottom: 8 }}>
          Your account is scheduled for deletion on{" "}
          <strong style={{ color: C.text }}>
            {purgeAt.toLocaleDateString("en-US", {
              month: "long", day: "numeric", year: "numeric",
            })}
          </strong>.
        </p>

        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 24 }}>
          {daysLeft > 0
            ? `You have ${daysLeft} day${daysLeft === 1 ? "" : "s"} left to restore it.`
            : "The grace period has ended."}
        </p>

        {error && (
          <div style={{
            padding: "10px 14px",
            background: C.red + "15",
            border: `1px solid ${C.red}40`,
            borderRadius: 6,
            fontSize: 12,
            color: C.red,
            marginBottom: 18,
          }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            onClick={handleRestore}
            disabled={submitting || daysLeft === 0}
            style={{
              padding: "12px 20px",
              background: daysLeft > 0 ? C.green : C.border,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              border: "none",
              borderRadius: 6,
              cursor: submitting || daysLeft === 0 ? "default" : "pointer",
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? "Restoring…" : "Restore my account"}
          </button>

          <Btn variant="ghost" onClick={onSignOut} disabled={submitting}>
            Sign out and continue with deletion
          </Btn>
        </div>

        <p style={{ fontSize: 11, color: C.muted, marginTop: 20, lineHeight: 1.5 }}>
          Restoring reactivates access to all your trades and settings. If your
          subscription was canceled, you can re-subscribe from the Billing page
          after restoring.
        </p>
      </div>
    </div>
  );
}
